import {ComputedRef, computed, Ref, onMounted, watch} from 'vue';
import type {NodeSerializerData} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/utils/CoreNodeSerializer';
import {CoreGraphNodeId} from '../../../../../../@polygonjs/polygonjs/src/core/graph/CoreGraph';
import {StoreController} from '../../../../store/controllers/StoreController';
import {NetworkNodeType, NodeContext} from '../../../../../../@polygonjs/polygonjs/src/engine/poly/NodeContext';
import {CodeData, CodeLanguage, CodeDisplayer} from '../helpers/updater/Common';

// types
import {SopType} from '../../../../../../@polygonjs/polygonjs/src/engine/poly/registers/nodes/types/Sop';
// base
import type {BaseNodeType} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/_Base';
// import {BaseGlShaderAssembler} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/gl/code/assemblers/_Base';
// gl nodes
import type {BaseBuilderMatNodeType} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/_BaseBuilder';
import type {BuilderCopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/cop/Builder';
import type {BuilderPostNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/post/Builder';
import type {ParticlesSystemGpuSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ParticlesSystemGpu';
// js nodes
import type {ActorSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Actor';
import type {ActorInstanceSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ActorInstance';
import type {ActorPointSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ActorPoint';
import type {ClothSolverSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ClothSolver';
import type {EntityBuilderSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EntityBuilder';
import type {ObjectBuilderSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ObjectBuilder';
import type {PhysicsDebugSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/PhysicsDebug';
import type {PhysicsPlayerSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/PhysicsPlayer';
import type {PhysicsWorldSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/PhysicsWorld';
import type {PointBuilderSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/PointBuilder';
import type {SDFBuilderSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/SDFBuilder';
import type {TetSoftBodySolverSopNode} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/TetSoftBodySolver';

export interface SetupDisplayedNodeOptions {
	currentJSONNode: ComputedRef<Readonly<NodeSerializerData> | null>;
	displayedNodeId: Ref<CoreGraphNodeId | null>;
	codeData: Ref<CodeData>;
}

function _isGlBuilderNode(node: BaseBuilderMatNodeType | BuilderPostNode | BuilderCopNode | ParticlesSystemGpuSopNode) {
	const builderMat = node as BaseBuilderMatNodeType;
	if (builderMat.assemblerController && builderMat.assemblerController()) {
		return true;
	}
	return false;
}
function _isJsBuilderNode(
	node:
		| ActorSopNode
		| ActorInstanceSopNode
		| ActorPointSopNode
		| ClothSolverSopNode
		| EntityBuilderSopNode
		| ObjectBuilderSopNode
		| PhysicsDebugSopNode
		| PhysicsPlayerSopNode
		| PhysicsWorldSopNode
		| PointBuilderSopNode
		| SDFBuilderSopNode
		| TetSoftBodySolverSopNode
) {
	const builderJs = node as ActorSopNode;
	if (builderJs.assemblerController && builderJs.assemblerController()) {
		return true;
	}
	return false;
}

export function SetupDisplayedNode(options: SetupDisplayedNodeOptions) {
	const {currentJSONNode, displayedNodeId, codeData} = options;

	onMounted(_checkDisplayedNodeId);
	const nodeId = computed(() => currentJSONNode.value?.graph_node_id);
	watch(nodeId, _checkDisplayedNodeId);

	function _checkDisplayedNodeId() {
		const id = nodeId.value;

		codeData.value.language = CodeLanguage.UNKNOWN;
		codeData.value.displayer = CodeDisplayer.UNKNOWN;

		if (id == null) {
			return;
		}
		const node = StoreController.engine.node(id);
		if (!node) {
			return;
		}

		const _applyActorNetwork = (_node: BaseNodeType) => {
			// we can't use the actor networks,
			// as they are only node containers, but do not actually generate code,
			// until they are referenced by an actor node
			// displayedNodeId.value = node.graphNodeId();
			// codeData.value.language = CodeLanguage.JS;
			// codeData.value.displayer = CodeDisplayer.ACTOR;
			return;
		};
		const _applyGlParent = (_node: BaseNodeType) => {
			const context = _node.context();
			const type = _node.type();
			switch (context) {
				case NodeContext.SOP: {
					switch (type) {
						case SopType.CLOTH_SOLVER: {
							displayedNodeId.value = _node.graphNodeId();
							codeData.value.language = CodeLanguage.GL;
							codeData.value.displayer = CodeDisplayer.MULTI;
							break;
						}
						case SopType.PARTICLES_SYSTEM_GPU: {
							displayedNodeId.value = _node.graphNodeId();
							codeData.value.language = CodeLanguage.GL;
							codeData.value.displayer = CodeDisplayer.MULTI;
							break;
						}
					}
				}
			}
		};
		const _applyJsParent = (_node: BaseNodeType) => {
			const context = _node.context();
			const type = _node.type();
			switch (context) {
				case NodeContext.SOP: {
					switch (type) {
						case SopType.ACTOR:
						case SopType.ACTOR_INSTANCE:
						case SopType.ACTOR_POINT:
						case SopType.ENTITY_BUILDER:
						case SopType.INSTANCE_BUILDER:
						case SopType.OBJECT_BUILDER:
						case SopType.POINT_BUILDER:
						case SopType.SDF_BUILDER: {
							displayedNodeId.value = _node.graphNodeId();
							codeData.value.language = CodeLanguage.JS;
							codeData.value.displayer = CodeDisplayer.ACTOR;
							return;
						}

						case SopType.TET_SOFT_BODY_SOLVER: {
							displayedNodeId.value = _node.graphNodeId();
							codeData.value.language = CodeLanguage.JS;
							codeData.value.displayer = CodeDisplayer.ACTOR_VELOCITY_COLLIDER;
							return;
						}
					}
				}
			}
		};

		if (node.type() == NetworkNodeType.ACTOR) {
			return _applyActorNetwork(node);
		}

		const context = node.context();

		switch (context) {
			case NodeContext.MAT: {
				const builderMat = node as BaseBuilderMatNodeType;
				if (_isGlBuilderNode(builderMat)) {
					displayedNodeId.value = builderMat.graphNodeId();
					codeData.value.language = CodeLanguage.GL;
					codeData.value.displayer = CodeDisplayer.VERTEX_AND_FRAGMENT;
					return;
				}
			}
			case NodeContext.GL: {
				codeData.value.language = CodeLanguage.GL;
				// check if parent is mat
				const parentMAT = node.parentController.firstAncestorWithContext(NodeContext.MAT);
				if (parentMAT) {
					const mat = parentMAT;
					const builderMat = mat as BaseBuilderMatNodeType;
					if (_isGlBuilderNode(builderMat)) {
						displayedNodeId.value = builderMat.graphNodeId();
						codeData.value.displayer = CodeDisplayer.VERTEX_AND_FRAGMENT;
						return;
					}
				}
				// check if parent is COP
				const parentCOP = node.parentController.firstAncestorWithContext(NodeContext.COP);
				if (parentCOP) {
					const cop = parentCOP;
					const builderCop = cop as BuilderCopNode;
					if (_isGlBuilderNode(builderCop)) {
						displayedNodeId.value = builderCop.graphNodeId();
						codeData.value.displayer = CodeDisplayer.FRAGMENT_ONLY;
						return;
					}
				}
				// check if parent is POST
				const parentPOST = node.parentController.firstAncestorWithContext(NodeContext.POST);
				if (parentPOST) {
					const post = parentPOST;
					const builderPost = post as BuilderPostNode;
					if (_isGlBuilderNode(builderPost)) {
						displayedNodeId.value = builderPost.graphNodeId();
						codeData.value.displayer = CodeDisplayer.FRAGMENT_ONLY;
						return;
					}
				}
				// check if parent is SOP
				const parentSOP = node.parentController.firstAncestorWithContext(NodeContext.SOP);
				if (parentSOP) {
					const sop = parentSOP;
					const particles = sop as ParticlesSystemGpuSopNode;
					if (_isGlBuilderNode(particles)) {
						displayedNodeId.value = particles.graphNodeId();
						codeData.value.displayer = CodeDisplayer.MULTI;
						return;
					}
				}
			}
			case NodeContext.JS: {
				codeData.value.language = CodeLanguage.JS;
				const parentSOP = node.parentController.firstAncestorWithContext(NodeContext.SOP);
				if (parentSOP) {
					if (_isJsBuilderNode(parentSOP as ActorSopNode)) {
						_applyJsParent(parentSOP);
						return;
					}
				} else {
					const parentActorsNetwork = node.parentController.findParent(
						(n) => n.type() == NetworkNodeType.ACTOR
					);
					if (parentActorsNetwork) {
						return _applyActorNetwork(parentActorsNetwork);
					}
				}
			}
			case NodeContext.SOP: {
				_applyJsParent(node);
				if (codeData.value.language != CodeLanguage.UNKNOWN) {
					return;
				}
				_applyGlParent(node);
			}
		}
	}

	return {};
}
